import React from 'react';
import { StepperProps } from '../../models';
import styles from './stepper.module.scss';

const Stepper: React.FC<StepperProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  error,
  warning,
  allowNegative = false,
  width = 'auto',
  name,
  id,
  required = false,
}) => {
  const stepperId = id || `stepper-${label?.toLowerCase().replace(/\s+/g, '-') || Math.random().toString(36).substr(2, 9)}`;

  const handleIncrement = () => {
    if (disabled) return;
    const newValue = value + step;
    if (max === undefined || newValue <= max) {
      onChange(newValue);
    }
  };

  const handleDecrement = () => {
    if (disabled) return;
    const newValue = value - step;
    const minValue = allowNegative ? (min ?? -Infinity) : (min ?? 0);
    if (newValue >= minValue) {
      onChange(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty input for editing
    if (inputValue === '' || inputValue === '-') {
      return;
    }

    const newValue = parseInt(inputValue, 10);
    
    if (!isNaN(newValue)) {
      const minValue = allowNegative ? (min ?? -Infinity) : (min ?? 0);
      const maxValue = max ?? Infinity;
      
      if (newValue >= minValue && newValue <= maxValue) {
        onChange(newValue);
      }
    }
  };

  const handleBlur = () => {
    // On blur, ensure value is valid
    const minValue = allowNegative ? (min ?? -Infinity) : (min ?? 0);
    if (isNaN(value) || value < minValue) {
      onChange(minValue);
    }
    if (max !== undefined && value > max) {
      onChange(max);
    }
  };

  const isDecrementDisabled = () => {
    const minValue = allowNegative ? (min ?? -Infinity) : (min ?? 0);
    return disabled || value <= minValue;
  };

  const isIncrementDisabled = () => {
    return disabled || (max !== undefined && value >= max);
  };

  return (
    <div 
      className={styles.stepperWrapper}
      style={{ width }}
    >
      {label && (
        <label htmlFor={stepperId} className={styles.stepperLabel}>
          {label}
          {required && <span className={styles.requiredIndicator}>*</span>}
        </label>
      )}
      
      <div className={`${styles.stepperContainer} ${error ? styles.error : ''} ${warning ? styles.warning : ''} ${disabled ? styles.disabled : ''}`}>
        <button
          type="button"
          className={`${styles.stepperButton} ${styles.decrementButton}`}
          onClick={handleDecrement}
          disabled={isDecrementDisabled()}
          aria-label="Decrement"
        >
          -
        </button>
        
        <input
          type="number"
          id={stepperId}
          name={name || stepperId}
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={styles.stepperInput}
          min={allowNegative ? min : (min ?? 0)}
          max={max}
          step={step}
          required={required}
        />
        
        <button
          type="button"
          className={`${styles.stepperButton} ${styles.incrementButton}`}
          onClick={handleIncrement}
          disabled={isIncrementDisabled()}
          aria-label="Increment"
        >
          +
        </button>
      </div>
      
      {error && (
        <span className={`${styles.message} ${styles.errorMessage}`}>
          {error}
        </span>
      )}
      
      {warning && !error && (
        <span className={`${styles.message} ${styles.warningMessage}`}>
          {warning}
        </span>
      )}
    </div>
  );
};

export default Stepper;

